import type { ThemeColorName } from '@examdojo/styles';
import type { BallPattern } from '../../simulation/urn/urn.model';

// ── Generic tree data model ──────────────────────────────

export interface TreeNodeContent {
  /** LaTeX or plain-text label rendered near the node, e.g. "R₁", "B₂". */
  readonly label?: string;
  /** Theme color for the node fill. */
  readonly color?: ThemeColorName;
  /** Ball pattern for SVG fill: 'solid' | 'hatched' | 'dotted'. */
  readonly pattern?: BallPattern;
}

export interface TreeChildEdge {
  /** Plain-text label on the edge, e.g. "3/5", "a", "b". */
  readonly label: string;
  /** Child node this edge connects to. */
  readonly node: TreeNodeDefinition;
}

export interface TreeNodeDefinition {
  /** Unique identifier for this node. */
  readonly id: string;
  /** Content rendered inside/near the node circle. */
  readonly content?: TreeNodeContent;
  /** Child edges (branches). Omit for leaf nodes. */
  readonly children?: readonly TreeChildEdge[];
}

export interface TreeDisplayConfig {
  /** Radius of tree nodes in SVG coordinate units. If omitted, auto-scales based on depth. */
  readonly nodeRadius?: number;
  /** Show computed probability labels below leaf nodes. */
  readonly showLeafProbabilities?: boolean;
  /** Show edge labels on leaf nodes (below the node circle). */
  readonly showLeafEdgeLabels?: boolean;
  /** Enable zoom controls and mouse wheel zoom. Defaults to true. */
  readonly zoomEnabled?: boolean;
  /** Enable hover highlighting on tree nodes. Defaults to true. */
  readonly hoverEnabled?: boolean;
}

export interface TreeParams {
  /** Root of the tree definition. */
  readonly root: TreeNodeDefinition;
  /** Display configuration overrides. */
  readonly display?: TreeDisplayConfig;
}

/** Path through the tree as a sequence of child indices (0-based). */
export type TreePath = readonly number[];

/** Per-path, per-depth color overrides for matching path highlighting. */
export type TreePathColors = readonly ThemeColorName[];

// ── Legacy binomial types (kept for backward compat) ─────

export const BINOMIAL_TREE_MAX_N = 6;

export interface BinomialTreeParams {
  readonly n: number;
  readonly k: number;
}

export const DEFAULT_BINOMIAL_TREE_PARAMS: BinomialTreeParams = {
  n: 3,
  k: 1,
};

export type BinaryPath = ReadonlyArray<'a' | 'b'>;
